import { action, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_USER_TENANT_ACCESS } from "src/api/user";

export interface TenantAccessRow {
    tenantid: string;
    tenancyid: string | null;
    role: number[];
    accesslevel: number | null;
    realestateid: string | null;
    realestatename: string | null;
    realestatenumber: string | null;
    unitid: string | null;
    unitname: string | null;
    unitnumber: string | null;
    leasestart: string | null;
    leaseend: string | null;
    tenantname: string | null;
}

interface TenantAccessApiRow extends Omit<TenantAccessRow, "role"> {
    role: number[] | number | null;
}

export class TenantAccessStore {
    rootStore: RootStore;
    tenantAccessRows: TenantAccessRow[] = [];

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            tenantAccessRows: observable,
            load: action,
            setTenantAccessRows: action,
            reset: action
        });
    }

    setTenantAccessRows = (rows: TenantAccessRow[]) => {
        this.tenantAccessRows = rows;
    };

    normalizeRoleIds = (roleValue: number[] | number | null | undefined): number[] => {
        if (Array.isArray(roleValue)) {
            return roleValue;
        }

        if (typeof roleValue === "number") {
            return [roleValue];
        }

        return [];
    };

    load = async (customerid: string, userid: string) => {
        if (!apolloClientInstance) {
            throw new Error("Apollo client not initialized.");
        }

        const { data } = await apolloClientInstance.query<
            {
                tenant_access: TenantAccessApiRow[];
            },
            {
                customerid: string;
                userid: string;
            }
        >({
            query: GET_USER_TENANT_ACCESS,
            variables: {
                customerid,
                userid
            },
            fetchPolicy: "cache-first"
        });

        this.setTenantAccessRows(
            (data?.tenant_access ?? []).map((row) => ({
                ...row,
                role: this.normalizeRoleIds(row.role)
            }))
        );
    };

    reset = () => {
        this.tenantAccessRows = [];
    };

    getRoleIds = (roleValue: number[] | number | null | undefined): number[] => {
        return this.normalizeRoleIds(roleValue);
    };

    matchesRoleFilter = (roleValue: number[] | number | null | undefined, selectedRole: string): boolean => {
        if (!selectedRole || selectedRole === "all") {
            return true;
        }

        const selectedRoleId = Number(selectedRole);
        if (Number.isNaN(selectedRoleId)) {
            return false;
        }

        return this.getRoleIds(roleValue).includes(selectedRoleId);
    };

    filteredTenantAccessRows = (searchQuery: string, selectedRole: string): TenantAccessRow[] => {
        const normalizedQuery = searchQuery.toLowerCase().trim();
        const roleLabelsById = this.rootStore.userAccessStore.roleLabelsById;

        return this.tenantAccessRows.filter((row) => {
            const roleIds = this.getRoleIds(row.role);
            const rolesLabel =
                roleIds.length > 0
                    ? roleIds.map((roleId) => roleLabelsById.get(roleId) ?? String(roleId)).join(", ")
                    : "";

            const searchText = `${row.realestatename ?? ""} ${row.realestatenumber ?? ""} ${row.unitname ?? ""} ${
                row.unitnumber ?? ""
            } ${row.tenantname ?? ""} ${rolesLabel} ${row.leasestart ?? ""} ${row.leaseend ?? ""}`
                .toLowerCase()
                .trim();

            const queryMatches = normalizedQuery.length === 0 || searchText.includes(normalizedQuery);
            const roleMatches = this.matchesRoleFilter(row.role, selectedRole);
            return queryMatches && roleMatches;
        });
    };
}
